// EVE-Memory-Store. V1-Pendant: Module-level _memoryCache + _rasCache + _globalRasCache.
// V3-Refactor: ein Zustand-Store hält die Caches mit TTL-Tracking. systemRisk wird
// persistiert (V1: localStorage `eve_system_risk_v1`).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  GlobalRasRule,
  MemoryEntry,
  RasRule,
  SystemRiskSnapshot,
} from '@/types/eve-memory';

export const EVE_MEMORY_STORE_KEY = 'eve_memory_v3';

/** V1 TTL: 5 min cache. */
export const MEMORY_CACHE_TTL_MS = 5 * 60 * 1000;

export interface EveMemoryState {
  memories: MemoryEntry[];
  rasRules: RasRule[];
  globalRasRules: GlobalRasRule[];
  systemRisk: SystemRiskSnapshot | null;
  /** Unix-ms wann zuletzt von Backend geladen. Pro Cache separat. */
  memoriesFetchedAt: number | null;
  rasRulesFetchedAt: number | null;
  globalRasFetchedAt: number | null;
}

export interface EveMemoryActions {
  setMemories: (entries: MemoryEntry[]) => void;
  setRasRules: (rules: RasRule[]) => void;
  setGlobalRasRules: (rules: GlobalRasRule[]) => void;
  /** Hängt eine optimistische Memory am Anfang an (Order: neueste zuerst). */
  prependMemory: (entry: MemoryEntry) => void;
  prependRasRule: (rule: RasRule) => void;
  removeMemory: (id: number | string) => void;
  removeRasRule: (id: number | string) => void;
  removeGlobalRasRule: (id: number | string) => void;
  setSystemRisk: (snapshot: SystemRiskSnapshot) => void;
  clearSystemRisk: () => void;
  /** Invalidiert alle Caches (V1 `invalidateMemoryCache`). */
  invalidateAll: () => void;
  /** Resettet komplett. */
  reset: () => void;
}

export type EveMemoryStore = EveMemoryState & EveMemoryActions;

const initialState: EveMemoryState = {
  memories: [],
  rasRules: [],
  globalRasRules: [],
  systemRisk: null,
  memoriesFetchedAt: null,
  rasRulesFetchedAt: null,
  globalRasFetchedAt: null,
};

export const useEveMemoryStore = create<EveMemoryStore>()(
  persist(
    (set) => ({
      ...initialState,

      setMemories: (memories) => set({ memories, memoriesFetchedAt: Date.now() }),
      setRasRules: (rasRules) => set({ rasRules, rasRulesFetchedAt: Date.now() }),
      setGlobalRasRules: (globalRasRules) =>
        set({ globalRasRules, globalRasFetchedAt: Date.now() }),

      prependMemory: (entry) =>
        set((s) => ({ memories: [entry, ...s.memories.filter((m) => m.id !== entry.id)] })),

      prependRasRule: (rule) =>
        set((s) => ({ rasRules: [rule, ...s.rasRules.filter((r) => r.id !== rule.id)] })),

      removeMemory: (id) => set((s) => ({ memories: s.memories.filter((m) => m.id !== id) })),
      removeRasRule: (id) => set((s) => ({ rasRules: s.rasRules.filter((r) => r.id !== id) })),
      removeGlobalRasRule: (id) =>
        set((s) => ({ globalRasRules: s.globalRasRules.filter((r) => r.id !== id) })),

      setSystemRisk: (snapshot) => set({ systemRisk: snapshot }),
      clearSystemRisk: () => set({ systemRisk: null }),

      invalidateAll: () =>
        set({
          memoriesFetchedAt: null,
          rasRulesFetchedAt: null,
          globalRasFetchedAt: null,
        }),

      reset: () => set({ ...initialState }),
    }),
    {
      name: EVE_MEMORY_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Nur systemRisk persistieren — Memories/RAS werden frisch vom Worker geladen
      partialize: (state) => ({ systemRisk: state.systemRisk }),
      version: 1,
    },
  ),
);

// ── Selektoren ───────────────────────────────────────────────────────────────

export const selectMemories = (s: EveMemoryState): MemoryEntry[] => s.memories;
export const selectRasRules = (s: EveMemoryState): RasRule[] => s.rasRules;
export const selectGlobalRasRules = (s: EveMemoryState): GlobalRasRule[] => s.globalRasRules;
export const selectSystemRisk = (s: EveMemoryState): SystemRiskSnapshot | null => s.systemRisk;

/** Prüft ob ein Cache abgelaufen ist (V1 TTL = 5min). */
export function isCacheStale(fetchedAt: number | null, now = Date.now()): boolean {
  if (fetchedAt === null) return true;
  return now - fetchedAt > MEMORY_CACHE_TTL_MS;
}
